import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Switch,
} from '@data-delivery/d3s';
import type { Control } from 'react-hook-form';
import type { UserFormValues } from '../schemas/user-form.schema';

interface UserIdentitySectionProps {
  control: Control<UserFormValues>;
  mode: 'create' | 'edit';
  isSystemUser: boolean;
}

export function UserIdentitySection({ control, mode, isSystemUser }: UserIdentitySectionProps) {
  return (
    <>
      <div className='rounded-md border p-4 bg-muted/40 text-sm leading-relaxed'>
        <p className='mb-2 font-medium'>Let's set up this SFTP user.</p>
        <p>
          Choose whether this is a <strong>system user</strong> (service account) or a regular human
          user. System users derive names automatically and require an owner; regular users must
          supply real names and a valid email login.
        </p>
      </div>

      <div className='grid grid-cols-12 gap-4 items-start'>
        <div className='col-span-12 md:col-span-3'>
          <FormField
            control={control}
            name='isSystemUser'
            render={({ field }) => (
              <FormItem className='flex flex-col gap-1'>
                <FormLabel>System User?</FormLabel>
                <FormControl>
                  <div className='flex items-center gap-3'>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                    <span className='text-xs text-muted-foreground'>
                      {field.value ? 'Technical account' : 'Human user'}
                    </span>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className='col-span-12 md:col-span-4'>
          <FormField
            control={control}
            name='loginId'
            render={({ field }) => (
              <FormItem className='flex flex-col gap-1'>
                <FormLabel>Login ID{mode === 'create' ? ' *' : ''}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type={isSystemUser ? 'text' : 'email'}
                    readOnly={mode === 'edit'}
                    disabled={mode === 'edit'}
                    aria-readonly={mode === 'edit'}
                    placeholder={isSystemUser ? 'system-identifier' : 'user@example.com'}
                  />
                </FormControl>
                {mode === 'edit' && (
                  <p className='text-xs text-muted-foreground'>Login ID cannot be changed.</p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {!isSystemUser && (
          <div className='col-span-12 md:col-span-5 grid grid-cols-2 gap-4'>
            <FormField
              control={control}
              name='firstName'
              render={({ field }) => (
                <FormItem className='flex flex-col gap-1'>
                  <FormLabel>First Name *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='Jane' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name='lastName'
              render={({ field }) => (
                <FormItem className='flex flex-col gap-1'>
                  <FormLabel>Last Name *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='Doe' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {isSystemUser && (
          <div className='col-span-12 md:col-span-5'>
            <FormField
              control={control}
              name='serviceAccountOwner'
              render={({ field }) => (
                <FormItem className='flex flex-col gap-1'>
                  <FormLabel>Service Account Owner *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='owner@example.com' />
                  </FormControl>
                  <FormMessage />
                  <p className='text-xs text-muted-foreground'>
                    Responsible contact for this system account.
                  </p>
                </FormItem>
              )}
            />
          </div>
        )}
      </div>
    </>
  );
}
